import React from "react";
import { lighten, darken } from "./colorUtils";

const BUBBLES = [
  { cx: 12, cy: 110, r: 6,  color: "#F0BE39", delay: -2,   dur: 18, waver: true,  grand: false },
  { cx: 28, cy: 120, r: 4,  color: "#3EB567", delay: -5,   dur: 14, waver: false, grand: false },
  { cx: 45, cy: 115, r: 7,  color: "#FF8F47", delay: -9,   dur: 20, waver: true,  grand: false },
  { cx: 62, cy: 125, r: 5,  color: "#FFFFFF", delay: -1,   dur: 16, waver: false, grand: false },
  { cx: 78, cy: 112, r: 8,  color: "#F0BE39", delay: -7,   dur: 22, waver: true,  grand: false },
  { cx: 92, cy: 120, r: 4,  color: "#3EB567", delay: -12,  dur: 17, waver: false, grand: false },
  { cx: 20, cy: 130, r: 3,  color: "#FF8F47", delay: -4,   dur: 13, waver: true,  grand: false },
  { cx: 54, cy: 128, r: 6,  color: "#F0BE39", delay: -8,   dur: 19, waver: false, grand: false },
  { cx: 70, cy: 135, r: 5,  color: "#FFFFFF", delay: -3.5, dur: 21, waver: true,  grand: false },
  { cx: 35, cy: 140, r: 4,  color: "#3EB567", delay: -11,  dur: 15, waver: false, grand: false },
  { cx: 85, cy: 140, r: 3,  color: "#FF8F47", delay: -6,   dur: 12, waver: true,  grand: false },
  { cx: 8,  cy: 125, r: 5,  color: "#FFFFFF", delay: -10,  dur: 18, waver: false, grand: false },
  { cx: 30, cy: 118, r: 11, color: "#F0BE39", delay: -15,  dur: 32, waver: true,  grand: true  },
  { cx: 75, cy: 132, r: 10, color: "#FFFFFF", delay: -8,   dur: 30, waver: false, grand: true  },
  { cx: 55, cy: 145, r: 12, color: "#FF8F47", delay: -22,  dur: 36, waver: true,  grand: true  },
];

const MICRO = [
  { cx: 25, cy: 120, r: 1.2, color: "#FFFFFF", delay: -1, dur: 10 },
  { cx: 48, cy: 130, r: 1.4, color: "#FFFFFF", delay: -4, dur: 12 },
  { cx: 74, cy: 125, r: 1.0, color: "#FFFFFF", delay: -7, dur: 9  },
  { cx: 15, cy: 140, r: 1.3, color: "#F0BE39", delay: -2, dur: 11 },
  { cx: 64, cy: 135, r: 1.1, color: "#3EB567", delay: -6, dur: 10 },
  { cx: 89, cy: 128, r: 1.2, color: "#FFFFFF", delay: -3, dur: 13 },
];

const CAUSTICS = [
  { cx: 25, cy: 30, rx: 35, ry: 10, delay: 0,   dur: 60 },
  { cx: 70, cy: 55, rx: 40, ry: 12, delay: -20, dur: 75 },
  { cx: 45, cy: 80, rx: 30, ry: 9,  delay: -40, dur: 65 },
];

export const BubblesBackground = ({ bgColor = "#3E8DFF" }) => {
  const topColor = lighten(bgColor, 0.3);
  const bottomColor = darken(bgColor, 0.4);
  return (
    <div className="absolute w-full h-full overflow-hidden">
      <svg
        preserveAspectRatio="xMidYMid slice"
        className="w-full h-full"
        viewBox="0 0 100 100"
      >
        <defs>
          <linearGradient id="bubbleBg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor={topColor} />
            <stop offset="55%" stopColor={bgColor} />
            <stop offset="100%" stopColor={bottomColor} />
          </linearGradient>
          <filter id="bubbleGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="0.6" />
          </filter>
          <filter id="bubbleShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="0.8" />
            <feOffset dx="0" dy="1" result="offsetblur" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.35" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="causticBlur" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="5" />
          </filter>
          <style>{`
            @keyframes bubbleDrift {
              0%   { transform: translate(0, 0)         scale(0.75); opacity: 0;   }
              10%  {                                                opacity: 0.9; }
              50%  { transform: translate(3px, -60px)    scale(1.05); opacity: 0.7; }
              90%  {                                                opacity: 0.25;}
              100% { transform: translate(-4px, -150px)  scale(0.55); opacity: 0;   }
            }
            @keyframes bubbleWaver {
              0%   { transform: translate(0, 0)          scale(0.7);  opacity: 0;   }
              8%   {                                                  opacity: 0.9; }
              15%  { transform: translate(6px, -22px)    scale(0.95); }
              30%  { transform: translate(-5px, -45px)   scale(1.02); }
              45%  { transform: translate(7px, -70px)    scale(1.05); opacity: 0.8; }
              60%  { transform: translate(-6px, -95px)   scale(1.0);  }
              75%  { transform: translate(4px, -120px)   scale(0.85); opacity: 0.4; }
              100% { transform: translate(-3px, -150px)  scale(0.5);  opacity: 0;   }
            }
            @keyframes bubbleMicro {
              0%   { transform: translate(0, 0);        opacity: 0;   }
              20%  {                                    opacity: 0.8; }
              100% { transform: translate(2px, -140px); opacity: 0;   }
            }
            @keyframes spinHighlight {
              0%   { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes causticDrift {
              0%,100% { transform: translateX(0)     translateY(0);  opacity: 0.05; }
              50%     { transform: translateX(15px)  translateY(-4px); opacity: 0.12; }
            }
            .bubble       { animation-name: bubbleDrift; transform-origin: center; transform-box: fill-box; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
            .bubble-waver { animation-name: bubbleWaver; }
            .bubble-micro { animation-name: bubbleMicro; animation-timing-function: linear; animation-iteration-count: infinite; transform-origin: center; transform-box: fill-box; }
            .highlight-spin { animation: spinHighlight 16s linear infinite; transform-origin: 0 0; }
            .caustic      { animation: causticDrift linear infinite; transform-origin: center; transform-box: fill-box; }
          `}</style>
        </defs>

        <rect x="0" y="0" width="100" height="100" fill="url(#bubbleBg)" />

        {CAUSTICS.map((c, i) => (
          <ellipse
            key={`c-${i}`}
            className="caustic"
            cx={c.cx}
            cy={c.cy}
            rx={c.rx}
            ry={c.ry}
            fill="#ffffff"
            filter="url(#causticBlur)"
            style={{ animationDelay: `${c.delay}s`, animationDuration: `${c.dur}s` }}
          />
        ))}

        {BUBBLES.map((b, i) => (
          <g
            key={`b-${i}`}
            className={`bubble ${b.waver ? "bubble-waver" : ""}`}
            style={{ animationDelay: `${b.delay}s`, animationDuration: `${b.dur}s` }}
          >
            <g transform={`translate(${b.cx}, ${b.cy})`} filter="url(#bubbleShadow)">
              <circle
                cx={0}
                cy={0}
                r={b.r * 1.9}
                fill={b.color}
                opacity={b.grand ? 0.18 : 0.12}
                filter="url(#bubbleGlow)"
              />
              <circle
                cx={0}
                cy={0}
                r={b.r}
                fill={b.color}
                opacity={b.grand ? 0.55 : 0.5}
              />
              <g className="highlight-spin">
                <circle
                  cx={-b.r * 0.35}
                  cy={-b.r * 0.35}
                  r={b.r * 0.3}
                  fill="#ffffff"
                  opacity="0.85"
                />
              </g>
            </g>
          </g>
        ))}

        {MICRO.map((m, i) => (
          <circle
            key={`m-${i}`}
            className="bubble-micro"
            cx={m.cx}
            cy={m.cy}
            r={m.r}
            fill={m.color}
            opacity="0.6"
            style={{ animationDelay: `${m.delay}s`, animationDuration: `${m.dur}s` }}
          />
        ))}
      </svg>
    </div>
  );
};
